import type { SupabaseClient } from '@supabase/supabase-js';
import {
  checkUsername,
  loadProfile,
  register,
  resendConfirmation,
  signIn,
  signOutUser,
  updatePassword,
  updateUsername,
} from './accountActions';
import { authErrorMessage } from './authError';
import { collectLocalSave, saveSyncEngine, wipeLocalSave } from './saveSync';
import { canOpenAdminPanel, roleBadge, type Role } from './roles';
import { buildAdminPanel } from './adminUi';
import { showBanPopup } from './banUi';
import { el, labeled } from './dom';
import { consentNote, legalLinkRow } from './legalLinks';
import { buildAgeGate, forgetBand, rememberedBand } from './ageGate';
import { buildRecoveryCodePanel } from './recoveryCodePanel';
import { registerNoEmail } from './registerNoEmail';
import { recoverAccount } from './recoverAccount';
import { deleteMyAccount, downloadJson, exportMyData } from './dataRights';
import { syntheticAddress } from '../config/ages';
import type { AgeBand } from '../config/ages';
import { PASSWORD_HINT } from './signupError';
import './account.css';

declare global {
  interface Window {
    __supabaseEnv?: { url: string; anonKey: string };
  }
}

type PanelName = 'register' | 'login' | 'profile' | 'admin' | 'recover';

let client: SupabaseClient | null = null;
let button: HTMLButtonElement | null = null;
// True from a successful interactive sign-in until its page reload (or its
// failure path). refreshIdentity is suppressed meanwhile so the account
// button only ever shows the username AFTER syncOnLogin's reload — the
// signal the e2e helpers (and users) read as "login settled". Without this,
// the pre-reload identity refresh races the reload.
let loginInFlight = false;
// A failed email-link verification (GoTrue error redirect) shown by the next
// login panel build — set once at init, consumed on first render.
let pendingLoginError: string | null = null;
// Captured at module load: supabase-js may consume the hash during its own
// URL detection, and module evaluation runs before the client is created.
const initialHash = typeof window !== 'undefined' ? window.location.hash : '';
let panelHost: HTMLDivElement | null = null;
let openPanel: PanelName | null = null;
let currentUsername: string | null = null;
let currentEmail = '';
let currentRole: Role = 'user';
let currentUserId: string | null = null;

/** The signed-in account's user id, or null for a guest (The Hub's stock salt). */
export function signedInUserId(): string | null {
  return currentUserId;
}

/** The signed-in account's role ('user' for a guest). */
export function signedInRole(): Role {
  return currentUserId ? currentRole : 'user';
}

/** The one Supabase client (null without VITE_SUPABASE_*). */
export function accountClient(): SupabaseClient | null {
  return client;
}

type IdentityListener = () => void;
const identityListeners = new Set<IdentityListener>();

/**
 * Run `fn` whenever the signed-in identity (or its role) is re-read — the
 * menu's admin-only INBOX button waits on this, since the profile loads after
 * the first frame. Returns the unsubscribe.
 */
export function onIdentityChange(fn: IdentityListener): () => void {
  identityListeners.add(fn);
  return () => identityListeners.delete(fn);
}

// Optional feature: without VITE_SUPABASE_* baked into the build this renders
// nothing at all and the game is untouched. main.ts owns the (single) client.
export function initAccountUi(accountClient: SupabaseClient | null): void {
  client = accountClient;
  const root = document.getElementById('account-root');
  if (!client || !root) return;

  // E2E-only: the auth e2e probes RLS over REST with the anon key.
  if (import.meta.env.VITE_E2E) {
    window.__supabaseEnv = {
      url: import.meta.env.VITE_SUPABASE_URL ?? '',
      anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '',
    };
  }

  button = document.createElement('button');
  button.className = 'account-button';
  button.dataset.testid = 'account-button';
  button.textContent = 'Sign in';
  button.addEventListener('click', () => {
    if (openPanel) closePanel();
    else showPanel(currentUsername ? 'profile' : 'login');
  });
  root.appendChild(button);

  panelHost = document.createElement('div');
  root.appendChild(panelHost);

  // setTimeout escapes the auth lock held during the callback — calling
  // client.auth.* directly from inside onAuthStateChange deadlocks supabase-js.
  client.auth.onAuthStateChange(() => {
    setTimeout(() => void refreshIdentity(), 0);
  });
  void refreshIdentity();

  // A dead confirmation link lands here as an error redirect. Silently
  // showing a signed-out page reads as "the game signed me out" — open the
  // login panel with an explanation instead, and clean the hash.
  const authError = authErrorMessage(initialHash);
  if (authError) {
    history.replaceState(null, '', window.location.pathname + window.location.search);
    pendingLoginError = authError;
    showPanel('login');
  }
}

// Debug/DemoScene hook — no-op when the account system is unconfigured.
export function showAccountPanel(panel: PanelName): void {
  if (!client) return;
  showPanel(panel);
}

async function refreshIdentity(): Promise<void> {
  if (!client || !button) return;
  if (loginInFlight) return;
  const profile = await loadProfile(client);
  // A banned identity gets the popup (OK signs out) — except mid-login, where
  // the login handler owns the UX with an inline error instead.
  if (profile?.banned && openPanel !== 'login') {
    showBanPopup(client);
  }
  currentUsername = profile?.username ?? null;
  currentEmail = profile?.email ?? '';
  currentRole = profile?.role ?? 'user';
  currentUserId = profile?.userId ?? null;
  button.replaceChildren(currentUsername ?? 'Sign in');
  const badge = currentUsername ? roleBadge(currentRole) : null;
  if (badge) {
    const tag = document.createElement('span');
    tag.className = `account-badge ${badge.className}`;
    tag.dataset.testid = 'account-badge';
    tag.textContent = badge.text;
    button.append(' ', tag);
  }
  // A signed-in/out flip while a panel is open leaves a stale panel; close it.
  if ((openPanel === 'profile' || openPanel === 'admin') && !currentUsername) closePanel();
  for (const fn of identityListeners) fn();
}

function closePanel(): void {
  panelHost?.replaceChildren();
  openPanel = null;
}

function showPanel(panel: PanelName): void {
  if (!panelHost) return;
  panelHost.replaceChildren(buildPanel(panel));
  openPanel = panel;
}

function availabilityWatcher(input: HTMLInputElement): HTMLDivElement {
  const status = el('div', { className: 'account-availability', testid: 'account-availability' });
  let timer: ReturnType<typeof setTimeout> | undefined;
  input.addEventListener('input', () => {
    clearTimeout(timer);
    status.textContent = '';
    status.className = 'account-availability';
    timer = setTimeout(() => {
      const name = input.value;
      if (!client || !name) return;
      void checkUsername(client, name).then((result) => {
        if (input.value !== name) return; // stale response
        status.textContent =
          result === 'ok'
            ? '✓ available'
            : result === 'taken'
              ? 'That username is taken'
              : 'Use 3–20 letters, digits or _';
        status.className = `account-availability ${result === 'ok' ? 'ok' : 'bad'}`;
      });
    }, 400);
  });
  return status;
}

function buildPanel(panel: PanelName): HTMLDivElement {
  if (panel === 'register') return buildRegisterPanel();
  if (panel === 'login') return buildLoginPanel();
  if (panel === 'recover') return buildRecoverPanel();
  if (panel === 'admin')
    return buildAdminPanel(client!, {
      role: currentRole,
      userId: currentUserId,
      onBack: () => showPanel('profile'),
    });
  return buildProfilePanel();
}

/**
 * Signup starts with the neutral age screen. The band decides which form the
 * player sees — and an under-14 never sees an email field at all, which is the
 * whole point: we cannot mishandle what we never collected.
 *
 * Spec: docs/superpowers/specs/2026-09-12-privacy-compliance-design.md §3, §4.
 */
function buildRegisterPanel(): HTMLDivElement {
  const band = rememberedBand();
  if (!band) return buildAgeGate(() => showPanel('register'));
  return band === 'child' ? buildNoEmailRegisterPanel('child') : buildEmailRegisterPanel();
}

function buildEmailRegisterPanel(): HTMLDivElement {
  const email = el('input', { type: 'email', autocomplete: 'email', testid: 'account-email' });
  const password = el('input', {
    type: 'password',
    autocomplete: 'new-password',
    testid: 'account-password',
  });
  // The rule, stated before the mistake: the raw GoTrue refusal used to be the
  // first time a player heard it.
  const passwordHint = el(
    'div',
    { className: 'account-hint', testid: 'account-password-hint' },
    PASSWORD_HINT,
  );
  // Optional, and the trade-off is stated rather than left to be discovered:
  // an email is the only way to reset a forgotten password. Without one the
  // recovery code is the only route back in.
  const emailHint = el(
    'div',
    { className: 'account-hint', testid: 'account-email-hint' },
    'Add one if you want to be able to reset your password. Leave it blank and ' +
      'you will get a secret code instead.',
  );
  const username = el('input', {
    type: 'text',
    maxLength: 20,
    testid: 'account-username',
  });
  const availability = availabilityWatcher(username);
  const error = el('div', { className: 'account-error', testid: 'account-error' });
  const notice = el('div', { className: 'account-notice', testid: 'account-notice' });
  const submit = el('button', { testid: 'account-submit' }, 'Create account');
  submit.addEventListener('click', () => {
    // One signup per click, none while one is in flight: a double tap here
    // was two signups, the second a resend that invalidated the first email's
    // link (prod 2026-09-07).
    if (!client || submit.disabled) return;
    submit.disabled = true;
    error.textContent = '';
    notice.textContent = '';
    // Collect from REAL localStorage, not saveStorage(): under ?debug that is
    // the sandbox, and a debug session's fake grants must not be transferred.
    // No email given? Same no-email path a child takes, with a general band.
    if (!email.value.trim()) {
      void registerNoEmail(
        client,
        username.value.trim(),
        password.value,
        'general',
        collectLocalSave(window.localStorage),
      ).then((r) => {
        if (!r.ok) {
          error.textContent = r.error;
          submit.disabled = false;
          return;
        }
        panelHost?.replaceChildren(
          buildRecoveryCodePanel(r.recoveryCode, () => window.location.reload()),
        );
      });
      return;
    }
    void register(
      client,
      email.value,
      password.value,
      username.value,
      window.location.origin,
      collectLocalSave(window.localStorage),
    ).then((r) => {
      if (!r.ok) {
        error.textContent = r.error;
        submit.disabled = false;
        return;
      }
      // The email is out: the form leaves with it, so the only mail action
      // left on screen is an explicit resend.
      if (r.needsConfirm) panelHost?.replaceChildren(buildAwaitConfirmPanel(email.value.trim()));
      else submit.disabled = false;
    });
  });
  const toLogin = el(
    'button',
    { className: 'account-secondary', testid: 'account-goto-login' },
    'I already have an account',
  );
  toLogin.addEventListener('click', () => showPanel('login'));
  return el(
    'div',
    { className: 'account-panel', testid: 'account-panel-register' },
    el('h2', {}, 'Create account'),
    labeled('Email (optional)', email),
    emailHint,
    labeled('Password', password),
    passwordHint,
    labeled('Player name', username),
    availability,
    error,
    notice,
    consentNote(),
    submit,
    toLogin,
    legalLinkRow(),
  );
}

/**
 * The under-14 signup. There is deliberately NO email field on this form - not
 * theirs, not a parent's. With no email collected and no way for players to
 * contact one another, we hold no personal information about the child, so
 * verified parental consent is never triggered.
 *
 * The cost lands on the next screen: no email means no reset link, so the
 * recovery code is the only way back in.
 */
function buildNoEmailRegisterPanel(band: AgeBand): HTMLDivElement {
  const username = el('input', { type: 'text', maxLength: 20, testid: 'account-username' });
  const password = el('input', {
    type: 'password',
    autocomplete: 'new-password',
    testid: 'account-password',
  });
  const passwordHint = el(
    'div',
    { className: 'account-hint', testid: 'account-password-hint' },
    PASSWORD_HINT,
  );
  const availability = availabilityWatcher(username);
  const error = el('div', { className: 'account-error', testid: 'account-error' });
  const submit = el('button', { testid: 'account-submit' }, 'Create account');

  submit.addEventListener('click', () => {
    if (!client || submit.disabled) return;
    submit.disabled = true;
    error.textContent = '';
    void registerNoEmail(
      client,
      username.value.trim(),
      password.value,
      band,
      collectLocalSave(window.localStorage),
    ).then((r) => {
      if (!r.ok) {
        error.textContent = r.error;
        submit.disabled = false;
        return;
      }
      panelHost?.replaceChildren(
        buildRecoveryCodePanel(r.recoveryCode, () => window.location.reload()),
      );
    });
  });

  const toLogin = el(
    'button',
    { className: 'account-secondary', testid: 'account-goto-login' },
    'I already have an account',
  );
  toLogin.addEventListener('click', () => showPanel('login'));

  return el(
    'div',
    { className: 'account-panel', testid: 'account-panel-register' },
    el('h2', {}, 'Create account'),
    el(
      'div',
      { className: 'account-warning', testid: 'account-realname-warning' },
      "Don't use your real name. Pick something invented instead - SpicyPickleKnight, TurboNugget, anything you like.",
    ),
    labeled('Player name', username),
    availability,
    labeled('Password', password),
    passwordHint,
    el(
      'div',
      { className: 'account-consent', testid: 'account-consent' },
      'We will not ask you for an email address, your real name, or anything else about you. We keep your player name, your password and your game progress, and nothing more.',
    ),
    error,
    submit,
    toLogin,
    legalLinkRow(),
  );
}

/**
 * Recover an account with a player name and a recovery code.
 *
 * This is the only way back for a player with no email, which is every
 * under-14 account. Setting a new password here also ROTATES the code, so the
 * replacement has to be shown and acknowledged exactly as at signup.
 */
function buildRecoverPanel(): HTMLDivElement {
  const username = el('input', { type: 'text', maxLength: 20, testid: 'recover-username' });
  const code = el('input', { type: 'text', testid: 'recover-code' });
  const newPassword = el('input', {
    type: 'password',
    autocomplete: 'new-password',
    testid: 'recover-new-password',
  });
  const error = el('div', { className: 'account-error', testid: 'account-error' });
  const submit = el('button', { testid: 'recover-submit' }, 'Get back in');

  submit.addEventListener('click', () => {
    if (!client || submit.disabled) return;
    submit.disabled = true;
    error.textContent = '';
    void recoverAccount(client, username.value, code.value, newPassword.value).then((r) => {
      if (!r.ok) {
        error.textContent = r.error;
        submit.disabled = false;
        return;
      }
      panelHost?.replaceChildren(
        buildRecoveryCodePanel(r.recoveryCode, () => window.location.reload()),
      );
    });
  });

  const back = el(
    'button',
    { className: 'account-secondary', testid: 'recover-back' },
    'Back to sign in',
  );
  back.addEventListener('click', () => showPanel('login'));

  return el(
    'div',
    { className: 'account-panel', testid: 'account-panel-recover' },
    el('h2', {}, 'Use your secret code'),
    el(
      'div',
      { className: 'account-hint' },
      'Type your player name and the code you wrote down, then pick a new password.',
    ),
    labeled('Player name', username),
    labeled('Secret code', code),
    labeled('New password', newPassword),
    el(
      'div',
      { className: 'account-hint' },
      'You will get a new code afterwards \u2014 the old one stops working.',
    ),
    error,
    submit,
    back,
  );
}

// Replaces the register form once the confirmation email is on its way. The
// form is gone on purpose: left in place, every extra click on Create account
// re-sent the email and invalidated the previous link, and once the address
// was confirmed it showed "Check your email" while nothing was sent (prod
// 2026-09-07: nine clicks in three minutes from one player).
function buildAwaitConfirmPanel(address: string): HTMLDivElement {
  const error = el('div', { className: 'account-error', testid: 'account-error' });
  const notice = el(
    'div',
    { className: 'account-notice', testid: 'account-notice' },
    'Check your email to confirm your account.',
  );
  const resend = el(
    'button',
    { className: 'account-secondary', testid: 'account-resend-confirm' },
    'Resend confirmation email',
  );
  resend.addEventListener('click', () => {
    if (!client || resend.disabled) return;
    resend.disabled = true;
    error.textContent = '';
    void resendConfirmation(client, address).then((r) => {
      resend.disabled = false;
      if (!r.ok) error.textContent = r.error;
      else notice.textContent = 'New confirmation email sent — use the newest email in your inbox.';
    });
  });
  const toLogin = el(
    'button',
    { className: 'account-secondary', testid: 'account-goto-login' },
    'I already have an account',
  );
  toLogin.addEventListener('click', () => showPanel('login'));
  return el(
    'div',
    { className: 'account-panel', testid: 'account-panel-confirm' },
    el('h2', {}, 'Check your email'),
    el('div', { className: 'account-email-display' }, address),
    notice,
    error,
    resend,
    toLogin,
  );
}

function buildLoginPanel(): HTMLDivElement {
  const email = el('input', { type: 'email', autocomplete: 'email', testid: 'account-email' });
  const password = el('input', {
    type: 'password',
    autocomplete: 'current-password',
    testid: 'account-password',
  });
  const error = el('div', { className: 'account-error', testid: 'account-error' });
  if (pendingLoginError) {
    error.textContent = pendingLoginError;
    pendingLoginError = null;
  }
  const warning = el('div', { className: 'account-warning', testid: 'account-sync-warning' });
  const notice = el('div', { className: 'account-notice', testid: 'account-notice' });
  // Shown only after a sign-in fails with "Email not confirmed" — re-sending
  // beats re-registering (which would invalidate the older links).
  const resend = el(
    'button',
    { className: 'account-secondary', hidden: true, testid: 'account-resend-confirm' },
    'Resend confirmation email',
  );
  const submit = el('button', { testid: 'account-submit' }, 'Sign in');
  // Guest progress on this device gets ONE explicit confirmation before it
  // can be replaced by the account's save: the first click warns, the second
  // proceeds. Rebuilding the panel (switching views) naturally re-arms it.
  let syncWarned = false;
  // One request per click: every extra tap on Sign in was another password
  // attempt against GoTrue's login rate limit (prod 2026-09-07, three in one
  // second). Both buttons wait together — a resend mid-sign-in is the same
  // spam by another name.
  const setBusy = (busy: boolean): void => {
    submit.disabled = busy;
    resend.disabled = busy;
  };
  submit.addEventListener('click', () => {
    if (!client || submit.disabled) return;
    error.textContent = '';
    notice.textContent = '';
    const engine = saveSyncEngine();
    if (engine && !engine.localIsFresh() && !syncWarned) {
      syncWarned = true;
      warning.textContent =
        'This device has guest progress. Signing in will replace it with your ' +
        "account's save (a brand-new account inherits it instead). " +
        'Click "Sign in" again to continue.';
      return;
    }
    setBusy(true);
    // A player with no email signs in with their player name. The '@' decides
    // which was typed; a name is turned into its synthetic address, which is
    // derived rather than looked up so no RPC can map names to accounts.
    const typed = email.value.trim();
    const identity = typed.includes('@') ? typed : syntheticAddress(typed);
    void signIn(client, identity, password.value).then(async (r) => {
      if (!r.ok) {
        error.textContent = r.error;
        if (/email not confirmed/i.test(r.error)) resend.hidden = false;
        setBusy(false);
        return;
      }
      loginInFlight = true;
      // Banned accounts authenticate fine — the ban lives on the profile.
      // Check BEFORE syncOnLogin: that pulls the account save and reloads the
      // page, which would skip this gate entirely.
      const profile = await loadProfile(client!);
      if (profile?.banned) {
        await signOutUser(client!);
        loginInFlight = false;
        error.textContent = "You've been banned";
        setBusy(false);
        return;
      }
      closePanel();
      // Pull/adopt the account save, then reload into it.
      await saveSyncEngine()?.syncOnLogin();
      // Normally unreachable (syncOnLogin reloads the page); belt for a
      // missing engine so the button can't stay suppressed forever.
      loginInFlight = false;
      void refreshIdentity();
    });
  });
  resend.addEventListener('click', () => {
    if (!client || resend.disabled) return;
    setBusy(true);
    error.textContent = '';
    notice.textContent = '';
    void resendConfirmation(client, email.value).then((r) => {
      setBusy(false);
      if (!r.ok) {
        error.textContent = r.error;
        return;
      }
      resend.hidden = true;
      notice.textContent = 'New confirmation email sent — use the newest email in your inbox.';
    });
  });
  // The only way back for a player with no email — every under-14 account.
  const forgot = el(
    'button',
    { className: 'account-secondary', testid: 'account-forgot' },
    'I forgot my password',
  );
  forgot.addEventListener('click', () => showPanel('recover'));

  const toRegister = el(
    'button',
    { className: 'account-secondary', testid: 'account-goto-register' },
    'Create an account',
  );
  toRegister.addEventListener('click', () => showPanel('register'));
  return el(
    'div',
    { className: 'account-panel', testid: 'account-panel-login' },
    el('h2', {}, 'Sign in'),
    labeled('Player name or email', email),
    labeled('Password', password),
    error,
    notice,
    warning,
    resend,
    submit,
    forgot,
    toRegister,
    legalLinkRow(),
  );
}

function buildProfilePanel(): HTMLDivElement {
  const error = el('div', { className: 'account-error', testid: 'account-error' });
  const notice = el('div', { className: 'account-notice', testid: 'account-notice' });

  const username = el('input', {
    type: 'text',
    maxLength: 20,
    value: currentUsername ?? '',
    testid: 'account-username-edit',
  });
  const availability = availabilityWatcher(username);
  const saveName = el('button', { testid: 'account-username-save' }, 'Save name');
  saveName.addEventListener('click', () => {
    if (!client) return;
    error.textContent = '';
    void updateUsername(client, username.value).then((r) => {
      if (!r.ok) error.textContent = r.error;
      else {
        notice.textContent = 'Player name updated.';
        void refreshIdentity();
      }
    });
  });

  const password = el('input', {
    type: 'password',
    autocomplete: 'new-password',
    testid: 'account-password-new',
  });
  const savePassword = el('button', { testid: 'account-password-save' }, 'Change password');
  savePassword.addEventListener('click', () => {
    if (!client) return;
    error.textContent = '';
    void updatePassword(client, password.value).then((r) => {
      if (!r.ok) error.textContent = r.error;
      else {
        password.value = '';
        notice.textContent = 'Password changed.';
      }
    });
  });

  // Role-gated entry; purely cosmetic — the admin RPCs re-check server-side.
  const adminEntry = canOpenAdminPanel(currentRole)
    ? el('button', { className: 'account-secondary', testid: 'account-goto-admin' }, 'Admin panel')
    : null;
  adminEntry?.addEventListener('click', () => showPanel('admin'));

  // The two rights the privacy policy promises. Before these existed both
  // meant emailing a human who would run SQL by hand.
  const exportBtn = el(
    'button',
    { className: 'account-secondary', testid: 'account-export' },
    'Download my data',
  );
  exportBtn.addEventListener('click', () => {
    if (!client) return;
    error.textContent = '';
    void exportMyData(client).then((r) => {
      if (!r.ok) {
        error.textContent = r.error;
        return;
      }
      downloadJson(r.data, `hambitower-${currentUsername ?? 'account'}.json`);
      notice.textContent = 'Your data has been downloaded.';
    });
  });

  // Deletion is irreversible, so it takes two deliberate steps rather than one
  // click: a child should not be able to destroy their progress by accident.
  const deleteBtn = el(
    'button',
    { className: 'account-danger', testid: 'account-delete' },
    'Delete my account',
  );
  const deleteConfirm = el('div', { className: 'account-confirm', hidden: true });
  const deleteYes = el(
    'button',
    { className: 'account-danger', testid: 'account-delete-confirm' },
    'Yes, delete everything',
  );
  const deleteNo = el(
    'button',
    { className: 'account-secondary', testid: 'account-delete-cancel' },
    'Keep my account',
  );
  deleteConfirm.append(
    el(
      'div',
      { className: 'recovery-warning' },
      'This deletes your account, your progress and everything we hold about ' +
        'you. It cannot be undone.',
    ),
    deleteYes,
    deleteNo,
  );
  deleteBtn.addEventListener('click', () => {
    deleteConfirm.hidden = false;
    deleteBtn.hidden = true;
  });
  deleteNo.addEventListener('click', () => {
    deleteConfirm.hidden = true;
    deleteBtn.hidden = false;
  });
  deleteYes.addEventListener('click', () => {
    if (!client || deleteYes.disabled) return;
    deleteYes.disabled = true;
    error.textContent = '';
    void deleteMyAccount(client).then((r) => {
      if (!r.ok) {
        error.textContent = r.error;
        deleteYes.disabled = false;
        return;
      }
      // The account is gone; the local save would otherwise re-sync into a
      // brand new account on the next signup.
      wipeLocalSave(window.localStorage);
      closePanel();
      window.location.reload();
    });
  });

  const logout = el(
    'button',
    { className: 'account-secondary', testid: 'account-logout' },
    'Log out',
  );
  logout.addEventListener('click', () => {
    if (!client) return;
    void (async () => {
      // Order matters: flush the last progress to the account BEFORE signing
      // out (afterwards there is no session) and BEFORE the wipe (a flush
      // after it would push an empty save over the account).
      await saveSyncEngine()?.syncBeforeLogout();
      await signOutUser(client!);
      wipeLocalSave(window.localStorage);
      // The age band belongs to a PERSON, not a device. Left behind, the next
      // person to sign up on a shared family machine inherits it — so a child
      // following a parent would be shown the adult form, with its email field,
      // and the whole gate would be defeated.
      forgetBand(window.localStorage);
      closePanel();
      window.location.reload();
    })();
  });

  return el(
    'div',
    { className: 'account-panel', testid: 'account-panel-profile' },
    el('h2', {}, 'Your profile'),
    el('div', { className: 'account-email-display' }, currentEmail),
    labeled('Player name', username),
    availability,
    saveName,
    labeled('New password', password),
    el('div', { className: 'account-hint' }, PASSWORD_HINT),
    savePassword,
    ...(adminEntry ? [adminEntry] : []),
    error,
    notice,
    exportBtn,
    logout,
    deleteBtn,
    deleteConfirm,
  );
}
